/**
 * PayrollFinanceApproval — Finance Approval Tab
 *
 * Add to PayrollManagement.js:
 *   import PayrollFinanceApproval from './PayrollFinanceApproval';
 *
 *   // Inside your tabs:
 *   { key: 'approval', label: 'Finance Approval' }
 *
 *   // Inside tab content:
 *   {activeTab === 'approval' && <PayrollFinanceApproval />}
 *
 * Shows:
 *   - List of payroll runs pending finance approval
 *   - Full preview (employee breakdown) before approving
 *   - Approve / Reject with reason
 *   - Approval history
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Badge, Button, Row, Col,
    Modal, Form, Spinner, Alert,
} from 'react-bootstrap';
import {
    CheckCircle, XCircle, Eye, RefreshCw,
    CreditCard, Users, TrendingUp, AlertCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const MONTH_NAMES = ['','January','February','March','April','May','June',
    'July','August','September','October','November','December'];

const fmt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatusBadge = ({ status }) => {
    const cfg = {
        pending_approval: { bg:'warning', text:'dark', label:'Pending Approval' },
        processing:       { bg:'info',    text:'white', label:'Processing'       },
        processed:        { bg:'success', text:'white', label:'Processed'        },
        approved:         { bg:'success', text:'white', label:'Approved'         },
        rejected:         { bg:'danger',  text:'white', label:'Rejected'         },
    };
    const c = cfg[status] || { bg:'secondary', text:'white', label: status };
    return <Badge bg={c.bg} text={c.text} style={{ fontSize:10 }}>{c.label}</Badge>;
};

// ════════════════════════════════════════════════════════════════════════════
const PayrollFinanceApproval = () => {
    const [tab,      setTab]     = useState('pending');
    const [pending,  setPending] = useState([]);
    const [history,  setHistory] = useState([]);
    const [loading,  setLoading] = useState(true);
    const [preview,  setPreview] = useState(null);   // { run, summary, payslips }
    const [previewLoading, setPreviewLoading] = useState(false);
    const [action,   setAction]  = useState(null);   // { run, type: 'approve'|'reject' }
    const [reason,   setReason]  = useState('');
    const [submitting, setSubmitting] = useState(false);

    const loadPending = useCallback(async () => {
        setLoading(true);
        try {
            const [pRes, hRes] = await Promise.all([
                axiosInstance.get('/payroll/pending-approval'),
                axiosInstance.get('/payroll/approval/history'),
            ]);
            setPending(pRes.data.data || []);
            setHistory(hRes.data.data || []);
        } catch { toast.error('Failed to load approval queue'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadPending(); }, [loadPending]);

    const openPreview = async (run) => {
        setPreviewLoading(true);
        setPreview(null);
        try {
            const res = await axiosInstance.get(`/payroll/approval/${run.id}`);
            setPreview(res.data.data);
        } catch { toast.error('Failed to load preview'); }
        finally { setPreviewLoading(false); }
    };

    const submitAction = async () => {
        if (!action) return;
        if (action.type === 'reject' && !reason.trim()) {
            toast.error('Please enter a reason for rejection'); return;
        }
        setSubmitting(true);
        try {
            const endpoint = `/payroll/approval/${action.run.id}/${action.type}`;
            await axiosInstance.post(endpoint, {
                remarks: reason || undefined,
                reason:  reason || undefined,
            });
            toast.success(`Payroll ${action.type === 'approve' ? 'approved ✓' : 'rejected'}`);
            setAction(null); setReason(''); setPreview(null);
            loadPending();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Action failed');
        } finally { setSubmitting(false); }
    };

    return (
        <div>
            {/* Tab switcher */}
            <div className="d-flex gap-2 mb-4">
                <Button size="sm"
                    variant={tab==='pending' ? 'primary' : 'outline-secondary'}
                    onClick={() => setTab('pending')}>
                    Pending Approval
                    {pending.length > 0 && (
                        <Badge bg="danger" className="ms-2" style={{ fontSize:9 }}>
                            {pending.length}
                        </Badge>
                    )}
                </Button>
                <Button size="sm"
                    variant={tab==='history' ? 'primary' : 'outline-secondary'}
                    onClick={() => setTab('history')}>
                    Approval History
                </Button>
                <Button size="sm" variant="outline-secondary"
                    className="ms-auto" onClick={loadPending}>
                    <RefreshCw size={13}/>
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary"/>
                </div>
            ) : tab === 'pending' ? (
                <>
                    {pending.length === 0 ? (
                        <Alert variant="success" className="text-center">
                            <CheckCircle size={20} className="me-2"/>
                            No payroll runs pending approval. All clear!
                        </Alert>
                    ) : (
                        <Card className="border-0 shadow-sm">
                            <Table hover responsive className="mb-0 align-middle small">
                                <thead className="bg-light">
                                    <tr className="text-uppercase text-muted" style={{ fontSize:10 }}>
                                        <th className="ps-4">Period</th>
                                        <th>Employees</th>
                                        <th>Total Gross</th>
                                        <th>Total Net</th>
                                        <th>Processed By</th>
                                        <th>Status</th>
                                        <th className="pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pending.map((run, i) => (
                                        <tr key={i}>
                                            <td className="ps-4 fw-bold">
                                                {MONTH_NAMES[run.month]} {run.year}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-1">
                                                    <Users size={13} className="text-muted"/>
                                                    {run.employee_count || '—'}
                                                </div>
                                            </td>
                                            <td className="fw-medium">{fmt(run.total_gross)}</td>
                                            <td className="text-success fw-bold">{fmt(run.total_net)}</td>
                                            <td className="text-muted">{run.processed_by_name || '—'}</td>
                                            <td><StatusBadge status={run.status}/></td>
                                            <td className="pe-4">
                                                <div className="d-flex gap-1 flex-wrap">
                                                    <Button size="sm" variant="outline-secondary"
                                                        className="py-0 px-2"
                                                        style={{ fontSize:11 }}
                                                        onClick={() => openPreview(run)}>
                                                        <Eye size={12} className="me-1"/>Preview
                                                    </Button>
                                                    <Button size="sm" variant="success"
                                                        className="py-0 px-2"
                                                        style={{ fontSize:11 }}
                                                        onClick={() => { setAction({ run, type:'approve' }); setReason(''); }}>
                                                        <CheckCircle size={12} className="me-1"/>Approve
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger"
                                                        className="py-0 px-2"
                                                        style={{ fontSize:11 }}
                                                        onClick={() => { setAction({ run, type:'reject' }); setReason(''); }}>
                                                        <XCircle size={12} className="me-1"/>Reject
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    )}
                </>
            ) : (
                /* History tab */
                <Card className="border-0 shadow-sm">
                    {history.length === 0 ? (
                        <div className="text-center text-muted py-5">No approval history yet.</div>
                    ) : (
                        <Table hover responsive className="mb-0 align-middle small">
                            <thead className="bg-light">
                                <tr className="text-uppercase text-muted" style={{ fontSize:10 }}>
                                    <th className="ps-4">Period</th>
                                    <th>Employees</th>
                                    <th>Total Net</th>
                                    <th>Actioned By</th>
                                    <th>Status</th>
                                    <th className="pe-4">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((run, i) => (
                                    <tr key={i}>
                                        <td className="ps-4 fw-bold">
                                            {MONTH_NAMES[run.month]} {run.year}
                                        </td>
                                        <td>{run.employee_count || '—'}</td>
                                        <td className="text-success fw-bold">{fmt(run.total_net)}</td>
                                        <td className="text-muted small">
                                            {run.status === 'rejected'
                                                ? (run.rejected_by_name || '—')
                                                : (run.approved_by_name || '—')}
                                        </td>
                                        <td><StatusBadge status={run.status}/></td>
                                        <td className="pe-4 small text-muted" style={{ maxWidth:200 }}>
                                            {run.status === 'rejected' && run.reject_reason
                                                ? <span className="text-danger">
                                                    ❌ {run.reject_reason.slice(0,80)}{run.reject_reason.length > 80 ? '…' : ''}
                                                  </span>
                                                : run.status === 'pending_approval'
                                                ? <span className="text-warning fw-bold">⏳ Awaiting approval</span>
                                                : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card>
            )}

            {/* ── Preview Modal ── */}
            <Modal show={!!preview || previewLoading}
                onHide={() => setPreview(null)}
                size="xl" centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">
                        Payroll Preview — {preview?.run
                            ? `${MONTH_NAMES[preview.run.month]} ${preview.run.year}`
                            : 'Loading…'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {previewLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary"/>
                        </div>
                    ) : preview && (
                        <>
                            {/* Summary cards */}
                            <Row className="g-3 mb-4">
                                {[
                                    { label:'Employees', value: preview.summary?.total_employees, icon: Users,     color:'#4f46e5' },
                                    { label:'Total Gross', value: fmt(preview.summary?.total_gross), icon: TrendingUp, color:'#0891b2' },
                                    { label:'Total TDS',  value: fmt(preview.summary?.total_tds),   icon: AlertCircle, color:'#f59e0b' },
                                    { label:'Total Net',  value: fmt(preview.summary?.total_net),   icon: CreditCard,  color:'#059669' },
                                ].map(({ label, value, icon: Icon, color }, i) => (
                                    <Col xs={6} lg={3} key={i}>
                                        <Card className="border-0 shadow-sm p-3 text-center">
                                            <div style={{ width:36, height:36, borderRadius:10,
                                                background:`${color}15`, margin:'0 auto 8px',
                                                display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                <Icon size={18} color={color}/>
                                            </div>
                                            <div className="fw-bold" style={{ fontSize:16 }}>{value}</div>
                                            <div className="text-muted" style={{ fontSize:11 }}>{label}</div>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>

                            {/* Employee breakdown */}
                            <div style={{ maxHeight:380, overflowY:'auto' }}>
                                <Table size="sm" hover className="mb-0" style={{ fontSize:12 }}>
                                    <thead className="bg-light sticky-top">
                                        <tr className="text-uppercase text-muted" style={{ fontSize:10 }}>
                                            <th>Employee</th>
                                            <th>Dept</th>
                                            <th>Gross</th>
                                            <th>PF</th>
                                            <th>TDS</th>
                                            <th>LOP Amt</th>
                                            <th className="text-success">Net Pay</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(preview.payslips || []).map((p, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <div className="fw-medium">{p.employee_name}</div>
                                                    <div className="text-muted" style={{ fontSize:10 }}>
                                                        {p.employee_code}
                                                    </div>
                                                </td>
                                                <td className="text-muted">{p.department_name || '—'}</td>
                                                <td>{fmt(p.gross_salary)}</td>
                                                <td>{fmt(p.pf_deduction)}</td>
                                                <td>{fmt(p.tds_deduction)}</td>
                                                <td>{fmt(p.lop_deduction)}</td>
                                                <td className="fw-bold text-success">{fmt(p.net_salary)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    {preview && (
                        <>
                            <Button variant="outline-danger"
                                onClick={() => {
                                    setPreview(null);
                                    setAction({ run: preview.run, type:'reject' });
                                    setReason('');
                                }}>
                                <XCircle size={14} className="me-1"/>Reject
                            </Button>
                            <Button variant="success"
                                onClick={() => {
                                    setPreview(null);
                                    setAction({ run: preview.run, type:'approve' });
                                    setReason('');
                                }}>
                                <CheckCircle size={14} className="me-1"/>Approve Payroll
                            </Button>
                        </>
                    )}
                    <Button variant="light" onClick={() => setPreview(null)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* ── Approve / Reject Confirm Modal ── */}
            <Modal show={!!action} onHide={() => setAction(null)} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">
                        {action?.type === 'approve' ? '✅ Approve Payroll' : '❌ Reject Payroll'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {action && (
                        <div className="p-3 rounded-3 mb-3" style={{ background:'#f8fafc' }}>
                            <div className="fw-bold">
                                {MONTH_NAMES[action.run?.month]} {action.run?.year}
                            </div>
                            <div className="text-muted small">
                                {action.run?.employee_count} employees ·
                                Total Net: {fmt(action.run?.total_net)}
                            </div>
                        </div>
                    )}

                    {action?.type === 'approve' ? (
                        <Alert variant="success" className="small py-2">
                            <CheckCircle size={14} className="me-1"/>
                            Approving will immediately release payslip notifications
                            to all <strong>{action?.run?.employee_count}</strong> employees.
                        </Alert>
                    ) : (
                        <>
                            <Alert variant="danger" className="small py-2">
                                <XCircle size={14} className="me-1"/>
                                The payroll run will be rejected. HR will be notified to review and re-run.
                            </Alert>
                            <Form.Group>
                                <Form.Label className="small fw-bold">
                                    Reason for Rejection <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control as="textarea" rows={3}
                                    placeholder="e.g. Incorrect salary for 3 employees — please review and re-run"
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}/>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={() => setAction(null)}>Cancel</Button>
                    <Button
                        variant={action?.type === 'approve' ? 'success' : 'danger'}
                        onClick={submitAction}
                        disabled={submitting}>
                        {submitting
                            ? <Spinner size="sm"/>
                            : action?.type === 'approve'
                                ? '✅ Confirm Approval'
                                : '❌ Confirm Rejection'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PayrollFinanceApproval;
